import moment from 'moment';
import accounting from 'accounting';
import { formatCNPJ } from '@/utils';
import { resolveCalculationTypeLabel } from '../constants/calculationTypes';
import { TARIFF_SOURCES, TARIFF_SOURCE_LABELS } from '../constants/tariffSources';
import { resolveWarningSeverity, CALCULATION_WARNINGS } from '../constants/calculationWarnings';

export const EMPTY_VALUE = '—';

const DATE_FORMAT = 'DD/MM/YYYY';
const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

export function formatMoney(value) {
  const amount = typeof value === 'number' && !isNaN(value) ? value : 0;
  return 'R$ ' + accounting.formatNumber(amount, 2, '.', ',');
}

export function formatText(value) {
  if (value === null || value === undefined) return EMPTY_VALUE;
  const text = String(value).trim();
  return text === '' ? EMPTY_VALUE : text;
}

function formatMoment(value, format) {
  if (!value) return EMPTY_VALUE;
  const parsed = moment(value, moment.ISO_8601);
  return parsed.isValid() ? parsed.format(format) : EMPTY_VALUE;
}

function unwrap(payload) {
  if (!payload) return {};
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
}

function buildTableLabel(table) {
  const controlNumber = table.control_number;
  const description = table.description;

  if (controlNumber && description) return controlNumber + ' — ' + description;
  if (description) return formatText(description);
  return formatText(controlNumber);
}

function buildOriginLabel(originCity) {
  if (!originCity || !originCity.name) return EMPTY_VALUE;
  return originCity.uf ? originCity.name + '/' + originCity.uf : originCity.name;
}

function buildTariffHint(source, vehicleTypeDescription) {
  const label = TARIFF_SOURCE_LABELS[source];
  if (!label) return null;

  const byVehicle =
    source === TARIFF_SOURCES.TRANSPORT_MEANS ||
    source === TARIFF_SOURCES.ROUTE_VALIDITY_VEHICLE;

  if (byVehicle && vehicleTypeDescription) {
    return label + ' (' + vehicleTypeDescription + ')';
  }

  return label;
}

function mapCarrier(carrier) {
  const source = carrier || {};

  return {
    name: formatText(source.name),
    document: source.document ? formatText(formatCNPJ(source.document)) : EMPTY_VALUE,
  };
}

function mapShipment(shipment) {
  const source = shipment || {};

  return {
    id: source.id || null,
    number: formatText(source.number),
    manifestNumber: formatText(source.manifest_number),
    dispatchDate: formatMoment(source.dispatch_date, DATE_FORMAT),
    carrier: mapCarrier(source.carrier),
  };
}

function mapTariff(tariff) {
  const source = tariff || {};
  const vehicleType = source.vehicle_type || {};

  return {
    value: formatMoney(source.value),
    vehicleType: formatText(vehicleType.description),
    hint: buildTariffHint(source.source, vehicleType.description),
  };
}

function mapFreight(freight) {
  const table = freight.table || {};
  const calculationType = freight.calculation_type || {};
  const route = freight.route || {};
  const itinerary = route.itinerary || {};

  return {
    table: {
      label: buildTableLabel(table),
      controlNumber: formatText(table.control_number),
      description: formatText(table.description),
      routeValidityId: table.route_validity_id || null,
    },
    calculationType: {
      code: calculationType.code || null,
      label: formatText(
        calculationType.description || resolveCalculationTypeLabel(calculationType.code)
      ),
    },
    calculationDate: formatMoment(freight.calculation_date, DATE_FORMAT),
    route: {
      label: formatText(route.label),
      itineraryCode: formatText(itinerary.code),
      originCity: buildOriginLabel(route.origin_city),
    },
    tariff: mapTariff(freight.tariff),
    totalValue: formatMoney(freight.total_value),
  };
}

function mapToll(toll) {
  const source = toll || {};

  return {
    value: formatMoney(source.value),
    voucherNumber: formatText(source.voucher_number),
  };
}

function mapManualExpense(manualExpense) {
  if (!manualExpense) return null;

  return {
    value: formatMoney(manualExpense.value),
  };
}

function resolveIsComplete(meta, hasFreight, hasManualExpense) {
  if (typeof meta.is_complete === 'boolean') return meta.is_complete;
  return hasFreight || hasManualExpense;
}

export function mapWarnings(warnings) {
  const list = Array.isArray(warnings) ? warnings : [];

  return list
    .filter(function (item) {
      return item && (item.message || item.code);
    })
    .map(function (item) {
      const code = item.code || null;
      const message = item.message ? String(item.message).trim() : '';

      return {
        code: code,
        message: message || 'Aviso não identificado' + (code ? ' (' + code + ')' : ''),
        severity: resolveWarningSeverity(code),
      };
    });
}

export function mapCalculationSheet(payload) {
  const raw = unwrap(payload);
  const meta = raw.meta || {};
  const hasFreight = Boolean(raw.freight);
  const hasManualExpense = Boolean(raw.manual_expense);
  const warnings = mapWarnings(raw.warnings);

  return {
    shipment: mapShipment(raw.shipment),
    freight: hasFreight ? mapFreight(raw.freight) : null,
    manualExpense: hasFreight ? null : mapManualExpense(raw.manual_expense),
    toll: mapToll(raw.toll),
    warnings: warnings,
    hasCarrierScopeNote: warnings.some(function (warning) {
      return warning.code === CALCULATION_WARNINGS.MULTIPLE_FREIGHT_TABLES;
    }),
    isComplete: resolveIsComplete(meta, hasFreight, hasManualExpense),
    generatedAt: formatMoment(meta.generated_at, DATE_TIME_FORMAT),
    tripLegId: meta.trip_leg_id || null,
  };
}
